import { html } from '../lib/html.js';
import { icon } from '../lib/icons.js';
import { getIndex, concept, person } from '../lib/content.js';
import { roman } from '../lib/format.js';
import { topbar, avatar, SPEAKERS } from '../components/chrome.js';
import { navigate } from '../lib/router.js';
import { deva } from './people.js';

const FEATURED_IDEAS = ['dharma', 'karma', 'yoga', 'atman', 'gunas', 'bhakti', 'maya', 'moksha'];
const FEATURED_PEOPLE = ['krishna', 'arjuna', 'sanjaya', 'dhritarashtra', 'duryodhana', 'bhishma', 'drona', 'karna'];

export default async function explore() {
  const idx = getIndex();
  return {
    title: 'Explore',
    html: html`<div class="explore">
      ${topbar({ title: 'Explore', search: false })}
      <header class="wrap page-head">
        <h1 class="h1">Explore</h1>
        <p class="lede">Look up any verse, idea or person, whenever a word stops you. New here? <a class="text-link" href="/guide">See how it works</a>.</p>
        <form class="search-field explore__search" role="search" action="/search" data-search-form>
          ${icon('search', { size: 20 })}
          <label class="sr-only" for="explore-q">Search the Gita</label>
          <input class="input" id="explore-q" name="q" type="search" placeholder="Try: duty, fear, 2.47, Arjuna" autocomplete="off" enterkeyhint="search">
        </form>
      </header>
      <div class="wrap">
        <section class="section" aria-labelledby="ideas-title">
          <div class="section__head"><h2 class="h3" id="ideas-title">Key ideas</h2><a class="link" href="/concepts">All ${idx.glossary.length} ${icon('chevron-right', { size: 16 })}</a></div>
          <div class="idea-grid">
            ${FEATURED_IDEAS.map((id) => concept(id)).filter(Boolean).map((g) => html`<a class="idea" href="/concept/${g.id}">
              <span class="idea__sa deva" lang="sa">${g.sa}</span>
              <span class="idea__term">${g.term}</span>
              <span class="idea__short">${g.short}</span>
            </a>`)}
          </div>
        </section>
        <section class="section" aria-labelledby="people-title">
          <div class="section__head"><h2 class="h3" id="people-title">Who's who</h2><a class="link" href="/people">Everyone ${icon('chevron-right', { size: 16 })}</a></div>
          <ul class="people-rail">
            ${FEATURED_PEOPLE.map((id) => person(id)).filter(Boolean).map((p) => html`<li><a class="person-card" href="/person/${p.id}">
              <span class="avatar avatar--lg${p.id === 'krishna' ? ' avatar--krishna' : ''}" aria-hidden="true">${deva(p)}</span>
              <span class="person-card__name">${p.name}</span>
              <span class="person-card__role">${p.role}</span>
            </a></li>`)}
          </ul>
        </section>
        <section class="section" id="famous" aria-labelledby="famous-title">
          <div class="section__head"><h2 class="h3" id="famous-title">Famous verses</h2></div>
          <div class="stack" data-famous>
            ${idx.famous.map((f, i) => html`<a class="card famous"${i >= 5 ? ' hidden' : ''} href="/verse/${f.ch}/${f.v}">
              <p class="famous__tr">${f.translation}</p>
              <p class="famous__foot">${avatar(f.speaker)}<span>${SPEAKERS[f.speaker] ? SPEAKERS[f.speaker].name : ''} · ${f.id}</span>${icon('arrow-right', { size: 16 })}</p>
            </a>`)}
          </div>
          ${idx.famous.length > 5 ? html`<button type="button" class="btn btn--secondary btn--block explore__more" data-more-famous>Show all ${idx.famous.length} famous verses</button>` : ''}
        </section>
        <section class="section" aria-labelledby="shape-title">
          <div class="section__head"><h2 class="h3" id="shape-title">The shape of the Gita</h2></div>
          <p class="muted" style="margin-bottom:16px">A traditional way to read the eighteen chapters is as three movements of six.</p>
          <div class="stack">
            ${idx.parts.map((p) => html`<a class="card card--pad shape" href="/journey#part-${p.id}">
              <span class="shape__num">${roman(p.id)}</span>
              <span class="shape__body"><span class="shape__title">${p.title} <span class="deva" lang="sa">${p.sa}</span></span><span class="shape__blurb">${p.blurb}</span><span class="shape__ch">Chapters ${p.chapters[0]} to ${p.chapters[5]}</span></span>
            </a>`)}
          </div>
        </section>
      </div>
    </div>`,
    mount(root) {
      const more = root.querySelector('[data-more-famous]');
      if (more) {
        more.addEventListener('click', () => {
          for (const a of root.querySelectorAll('[data-famous] [hidden]')) a.hidden = false;
          more.remove();
        });
      }
      if (location.hash === '#famous' && more) more.click();
      root.querySelector('[data-search-form]').addEventListener('submit', (e) => {
        e.preventDefault();
        const q = root.querySelector('#explore-q').value.trim();
        navigate(`/search${q ? `?q=${encodeURIComponent(q)}` : ''}`);
      });
    },
  };
}
